import type {HighlightItem} from '../../templates/custom/video-highlight/types';
import type {Position} from '../../templates/custom/video-highlight/types';
import {
  getHighlightActiveDuration,
  getHighlightEndTime,
  isHighlightActiveAtTime,
} from '../../templates/custom/video-highlight/highlightTiming';

export {isHighlightActiveAtTime};

export const DEFAULT_MARKER_COLOR = '#f52e2e';
export const DEFAULT_FOCUS_BORDER_COLOR = '#00ff00';
export const DEFAULT_FOCUS_BORDER_WIDTH = 3;
export const DEFAULT_ARROW_COLOR = '#ff0000';

export interface ResolvedMarkerRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const resolveMarkerRect = ({
  position,
  compositionWidth,
  compositionHeight,
  coordinateWidth,
  coordinateHeight,
}: {
  position: Position;
  compositionWidth: number;
  compositionHeight: number;
  coordinateWidth?: number | null;
  coordinateHeight?: number | null;
}): ResolvedMarkerRect => {
  if (position.unit === 'percent') {
    return {
      x: position.x * compositionWidth,
      y: position.y * compositionHeight,
      width: position.width * compositionWidth,
      height: position.height * compositionHeight,
    };
  }

  const scaleX =
    coordinateWidth != null && coordinateWidth > 0
      ? compositionWidth / coordinateWidth
      : 1;
  const scaleY =
    coordinateHeight != null && coordinateHeight > 0
      ? compositionHeight / coordinateHeight
      : 1;

  return {
    x: position.x * scaleX,
    y: position.y * scaleY,
    width: position.width * scaleX,
    height: position.height * scaleY,
  };
};

export const applyMarkerEasing = (
  progress: number,
  easingType: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out',
): number => {
  switch (easingType) {
    case 'ease-in':
      return progress * progress;
    case 'ease-out':
      return progress * (2 - progress);
    case 'ease-in-out':
      return progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;
    case 'linear':
    default:
      return progress;
  }
};

export const resolveFadeIn = (
  highlight: HighlightItem,
  defaultFadeIn: number,
): number => {
  return highlight.fadeIn ?? defaultFadeIn;
};

export const resolveFadeOut = (
  highlight: HighlightItem,
  defaultFadeOut: number,
): number => {
  return highlight.fadeOut ?? defaultFadeOut;
};

export const getMarkerOpacityAtTime = ({
  highlight,
  currentTimeInSeconds,
  defaultFadeIn,
  defaultFadeOut,
}: {
  highlight: HighlightItem;
  currentTimeInSeconds: number;
  defaultFadeIn: number;
  defaultFadeOut: number;
}): number => {
  const startTime = highlight.startTime;
  const endTime = getHighlightEndTime(highlight);

  if (currentTimeInSeconds < startTime || currentTimeInSeconds >= endTime) {
    return 0;
  }

  const fadeIn = resolveFadeIn(highlight, defaultFadeIn);
  const fadeOut = resolveFadeOut(highlight, defaultFadeOut);
  const timeSinceStart = currentTimeInSeconds - startTime;
  const timeUntilEnd = endTime - currentTimeInSeconds;

  const fadeInOpacity =
    fadeIn > 0 ? Math.min(timeSinceStart / fadeIn, 1) : 1;
  const fadeOutOpacity =
    fadeOut > 0 ? Math.min(timeUntilEnd / fadeOut, 1) : 1;

  return Math.max(0, Math.min(1, fadeInOpacity, fadeOutOpacity));
};

export const getFocusAnimationProgressAtTime = ({
  highlight,
  currentTimeInSeconds,
  defaultFadeIn,
  defaultFadeOut,
}: {
  highlight: HighlightItem;
  currentTimeInSeconds: number;
  defaultFadeIn: number;
  defaultFadeOut: number;
}): number => {
  if (!isHighlightActiveAtTime(highlight, currentTimeInSeconds)) {
    return 0;
  }

  const relativeTime = currentTimeInSeconds - highlight.startTime;
  const totalDuration = getHighlightActiveDuration(highlight);
  const easeInDuration = resolveFadeIn(highlight, defaultFadeIn);
  const easeOutDuration = resolveFadeOut(highlight, defaultFadeOut);
  const holdStartTime =
    easeInDuration > 0 ? Math.min(easeInDuration, totalDuration / 2) : 0;
  const holdEndTime = Math.max(
    holdStartTime,
    totalDuration - (easeOutDuration > 0 ? easeOutDuration : 0),
  );
  const easingType = highlight.easingType ?? 'ease-in-out';

  if (holdStartTime > 0 && relativeTime < holdStartTime) {
    return applyMarkerEasing(relativeTime / holdStartTime, easingType);
  }

  if (relativeTime < holdEndTime) {
    return 1;
  }

  if (relativeTime < totalDuration) {
    const zoomOutSpan = totalDuration - holdEndTime;
    if (zoomOutSpan <= 0) {
      return 0;
    }

    const progress = (relativeTime - holdEndTime) / zoomOutSpan;
    return 1 - applyMarkerEasing(progress, easingType);
  }

  return 0;
};
